import { supabase } from './supabaseClient';

const FOLDER_NAME = 'LIHTAR';

// Помилка протухшого токена
class TokenExpiredError extends Error {
  constructor() {
    super('Сесія закінчилась. Будь ласка, перелогіньтесь.');
    this.name = 'TokenExpiredError';
  }
}

// Перевірка відповіді на помилку авторизації
const checkAuthError = (response) => {
  if (response.status === 401 || response.status === 403) {
    throw new TokenExpiredError();
  }
};

// Отримати Google access token з сесії Supabase
const getAccessToken = async () => {
  let { data: { session } } = await supabase.auth.getSession();
  
  // Якщо provider_token немає — пробуємо рефрешнути сесію
  if (!session?.provider_token) {
    const { data, error } = await supabase.auth.refreshSession();
    if (!error && data?.session) {
      session = data.session;
    }
  }
  
  return session?.provider_token || null;
};

// Знайти або створити папку LIHTAR на Google Drive
export const getOrCreateFolder = async (accessToken, folderName = FOLDER_NAME, parentId = null) => {
  // Шукаємо існуючу папку
  let query = `name='${folderName}' and mimeType='application/vnd.google-apps.folder' and trashed=false`;
  if (parentId) {
    query += ` and '${parentId}' in parents`;
  }
  
  const searchRes = await fetch(
    `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name)`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  checkAuthError(searchRes);
  const searchData = await searchRes.json();
  
  if (searchData.files && searchData.files.length > 0) {
    return searchData.files[0].id;
  }
  
  // Створюємо нову папку
  const metadata = {
    name: folderName,
    mimeType: 'application/vnd.google-apps.folder'
  };
  if (parentId) {
    metadata.parents = [parentId];
  }
  
  const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(metadata)
  });
  checkAuthError(createRes);
  const folder = await createRes.json();
  return folder.id;
};

// Отримати або створити папку для проєкту: LIHTAR / {projectName}
const getProjectFolderId = async (accessToken, projectName) => {
  const rootFolderId = await getOrCreateFolder(accessToken, FOLDER_NAME);
  const projectFolderId = await getOrCreateFolder(accessToken, projectName, rootFolderId);
  return projectFolderId;
};

// Завантажити файл на Google Drive
export const uploadFile = async (file, projectName, subfolder = null) => {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('Немає доступу до Google Drive. Перелогіньтесь.');
  }
  
  try {
    // Отримуємо папку проєкту
    let folderId = await getProjectFolderId(accessToken, projectName);
    
    // Якщо є підпапка (наприклад "Чеки", "Фото")
    if (subfolder) {
      folderId = await getOrCreateFolder(accessToken, subfolder, folderId);
    }
    
    // Завантажуємо файл через multipart upload
    const metadata = {
      name: file.name,
      parents: [folderId]
    };
    
    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', file);
    
    const uploadRes = await fetch(
      'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,mimeType,size,webViewLink,thumbnailLink,createdTime',
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: form
      }
    );
    checkAuthError(uploadRes);
    
    if (!uploadRes.ok) {
      const err = await uploadRes.json();
      throw new Error(err.error?.message || 'Помилка завантаження');
    }
    
    return await uploadRes.json();
  } catch (e) {
    console.error('Помилка завантаження на Google Drive:', e);
    throw e;
  }
};

// Отримати список файлів проєкту
export const listFiles = async (projectName, subfolder = null) => {
  const accessToken = await getAccessToken();
  if (!accessToken) return [];
  
  try {
    let folderId = await getProjectFolderId(accessToken, projectName);
    
    if (subfolder) {
      folderId = await getOrCreateFolder(accessToken, subfolder, folderId);
    }
    
    const res = await fetch(
      `https://www.googleapis.com/drive/v3/files?q='${folderId}'+in+parents+and+trashed=false&fields=files(id,name,mimeType,size,webViewLink,thumbnailLink,createdTime)&orderBy=createdTime desc`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    checkAuthError(res);
    
    const data = await res.json();
    return data.files || [];
  } catch (e) {
    console.error('Помилка отримання файлів:', e);
    return [];
  }
};

// Видалити файл з Google Drive
export const deleteFile = async (fileId) => {
  const accessToken = await getAccessToken();
  if (!accessToken) throw new TokenExpiredError();
  
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files/${fileId}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` }
    }
  );
  checkAuthError(res);
  
  if (!res.ok && res.status !== 204) {
    throw new Error('Помилка видалення файлу');
  }
  return true;
};

// Отримати превʼю / thumbnail URL
export const getThumbnail = async (fileId) => {
  const accessToken = await getAccessToken();
  if (!accessToken) return null;
  
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files/${fileId}?fields=thumbnailLink,webContentLink`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  
  if (res.status === 401 || res.status === 403) {
    return null; // Тихо повертаємо null для thumbnail
  }
  
  const data = await res.json();
  return data.thumbnailLink || null;
};

// Завантажити файл (отримати download URL)
export const getDownloadUrl = async (fileId) => {
  const accessToken = await getAccessToken();
  if (!accessToken) return null;
  
  return `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media&access_token=${accessToken}`;
};

// Завантажити файл як blob URL (для превʼю зображень в <img>)
export const getFileBlobUrl = async (fileId) => {
  const accessToken = await getAccessToken();
  if (!accessToken) return null;
  
  try {
    const res = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch (e) {
    console.error('Помилка завантаження файлу:', e);
    return null;
  }
};

// Перейменувати папку/файл на Google Drive
export const renameFile = async (fileId, newName) => {
  const accessToken = await getAccessToken();
  if (!accessToken) return false;
  
  try {
    const res = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newName })
      }
    );
    checkAuthError(res);
    return res.ok;
  } catch (e) {
    console.error('Помилка перейменування:', e);
    return false;
  }
};

// Завантажити файл напряму в папку по ID (без пошуку)
export const uploadFileToFolder = async (file, folderId) => {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('Немає доступу до Google Drive. Перелогіньтесь.');
  }
  
  const metadata = {
    name: file.name,
    parents: [folderId]
  };
  
  const form = new FormData();
  form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
  form.append('file', file);
  
  const uploadRes = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,mimeType,size,webViewLink,thumbnailLink,createdTime',
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: form
    }
  );
  checkAuthError(uploadRes);
  
  if (!uploadRes.ok) {
    const err = await uploadRes.json();
    throw new Error(err.error?.message || 'Помилка завантаження');
  }
  
  return await uploadRes.json();
};
